import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from 'axios'
import { feedback } from '@/lib/feedback'
import { useAuthStore } from '@/stores/auth'

// 响应数据类型
export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
  error_code?: string
  pagination?: {
    total: number
    current: number
    page_size: number
  }
}

// 分页查询参数
export interface PaginationParams {
  page: number
  page_size: number
  search?: string
  filters?: Record<string, any>
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

// 401 时跳转登录页的方式，由路由注册（带上原目标页）；默认整页跳转
let unauthorizedHandler: () => void = () => {
  globalThis.location.href = '/login'
}

export function setUnauthorizedHandler(handler: () => void) {
  unauthorizedHandler = handler
}

function isLoginRequest(url: string | undefined) {
  return !!url && url.split('?')[0].endsWith('/auth/login')
}

// 错误提示走 feedback（跟随主题）；尚未挂载 UI 时只打印
function showError(text: string) {
  let messageApi: typeof feedback.message
  try {
    messageApi = feedback.message
  } catch {
    console.error(text)
    return
  }
  messageApi.error(text)
}

// 创建axios实例
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: Number.parseInt(import.meta.env.VITE_API_TIMEOUT) || 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // 添加管理员标识
    config.headers['X-Admin-Request'] = 'true'

    // 添加请求时间戳
    config.headers['X-Request-Time'] = Date.now().toString()

    // 在开发环境下打印请求信息
    if (import.meta.env.DEV) {
      console.log('🚀 Admin API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        params: config.params,
        data: config.data,
        token: token ? `Bearer ${token.substring(0, 20)}...` : 'No token'
      })
    }

    return config
  },
  (error) => {
    console.error('请求拦截器错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    // 在开发环境下打印响应信息
    if (import.meta.env.DEV) {
      console.log('✅ Admin API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      })
    }
    
    // 智能处理不同响应格式
    const data = response.data
    
    // JWT响应格式（登录、刷新token）
    if (data && typeof data === 'object' && 'access_token' in data) {
      // JWT格式直接通过，不需要额外处理
      return response
    }
    
    // 标准业务响应格式
    if (data && typeof data === 'object' && 'success' in data) {
      if (!data.success) {
        const errorMessage = data.message || '请求失败'
        showError(errorMessage)
        return Promise.reject(new Error(errorMessage))
      }
      // 成功响应，返回完整响应
      return response
    }
    
    // 其他格式（向后兼容）
    return response
  },
  (error) => {
    console.error('❌ Admin API Error:', error)
    
    // 处理不同类型的错误
    if (error.response) {
      const { status, data } = error.response
      
      switch (status) {
        case 401:
          // 登录接口的 401 是用户名或密码错误，由登录页提示，不当作登录过期处理
          if (isLoginRequest(error.config?.url)) break
          showError('登录已过期，请重新登录')
          useAuthStore.getState().clearAuth()
          unauthorizedHandler()
          break
          
        case 403:
          showError('没有访问权限')
          break
          
        case 404:
          showError('请求的资源不存在')
          break
          
        case 422: {
          const errorMessage = data?.message || '请求参数错误'
          showError(errorMessage)
          break
        }
        
        case 500:
          showError('服务器内部错误')
          break
          
        case 502:
        case 503:
        case 504:
          showError('服务暂时不可用，请稍后重试')
          break
          
        default:
          showError(data?.message || '网络错误，请稍后重试')
      }
    } else if (error.request) {
      // 网络错误
      showError('网络连接失败，请检查网络设置')
    } else {
      // 其他错误
      showError(error.message || '未知错误')
    }
    
    return Promise.reject(error)
  }
)

// 封装常用请求方法
export const request = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> => {
    return apiClient.get(url, config)
  },
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> => {
    return apiClient.post(url, data, config)
  },
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> => {
    return apiClient.put(url, data, config)
  },
  
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> => {
    return apiClient.patch(url, data, config)
  },
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> => {
    return apiClient.delete(url, config)
  }
}

// 文件上传
export const uploadFile = (
  url: string, 
  file: File, 
  onProgress?: (progress: number) => void
): Promise<AxiosResponse<ApiResponse>> => {
  const formData = new FormData()
  formData.append('file', file)
  
  return apiClient.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total && onProgress) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress)
      }
    }
  })
}

// 文件下载
export const downloadFile = async (
  url: string, 
  filename?: string,
  params?: any
): Promise<void> => {
  try {
    const response = await apiClient.get(url, {
      params,
      responseType: 'blob'
    })
    
    // 创建下载链接
    const blob = new Blob([response.data])
    const downloadUrl = globalThis.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    link.remove()
    globalThis.URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    console.error('文件下载失败:', error)
    showError('文件下载失败')
  }
}

// JWT认证专用请求（不包装响应）
export const authRequest = {
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> => {
    return apiClient.post(url, data, config)
  }
}

export default apiClient
export { apiClient }
